import {
    Children,
    Fragment,
    type HTMLAttributeAnchorTarget,
    isValidElement,
    type ReactNode,
    useMemo,
} from "react";

import { type TBTStatusBadgeDisplayTypes } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import {
    BdsActionBar,
    BdsButtonGroup,
    BdsContentHeader,
    type BdsContentHeaderProps,
    BdsIconClockCounterClockwise,
    BdsIconExport,
    BdsIconFunnel,
    BdsIconGiveFeedback,
    BdsIconHelp,
    BdsIconPlus,
    BdsIconPrinter,
    BdsIconSettings,
    type BdsLocationDescriptor,
    BdsRollupButton,
} from "@buildertrend/components";

import { BdsListPageHeaderFilterButton } from "commonComponents/bdsWrappers/BdsListPageHeader/BdsListPageHeaderFilterButton";
import { HotkeyCommands } from "commonComponents/utilities/Hotkey/hotkey.utility";

import "./BdsListPageHeader.less";

/** Determines the order that the icons will be rendered in. */
const iconVariantOrder = [
    "feedback",
    "help",
    "settings",
    "activity",
    "history",
    "print",
    "export",
    "filter",
] as const;
type PredefinedIconVariant = (typeof iconVariantOrder)[number];

/** This is the object composed from our predefined values found in mappedIconActions, and the IconAction that the user passes in. */
type IFullIconActionProps = {
    icon: ReactNode;
    tooltip?: string;
    displayType: "primary" | "secondary" | "tertiary";
    id: string;
    testid: string;
    minStage?: number;
    value: string;
    text: string;
    ariaLabel: string;
} & IClickable;

export type IIconAction = {
    icon: PredefinedIconVariant;
    isHidden?: boolean;
    selected?: boolean;
    loading?: boolean;
} & IClickable;

export type IBdsListPageHeader = {
    /** Ids and data-testids within the component will be based on this id, as they must be unique. */
    id?: string;
    /** Rendered in the top left of the component. */
    title: string;
    /** Rendered directly above the title. If undefined, no space is reserved and the title will be aligned to the top. */
    jobName?: ReactNode;
    testid: string;
    className?: string;
    /**
     * Displayed next to the job name:
     *
     * Job Name • Entity Type
     */
    entityType?: string;
    /** Data to render action items to the left of secondaryButtons node.  iconActions are sorted internally. */
    iconActions?: IIconAction[];
    /** Should be 1-2 BdsRollupButtons with displayType "secondary". Renders left of primaryButtons node and right of iconActions. */
    secondaryButtons?: ReactNode;
    /** Properties used to construct the primary action's BdsRollupButton.  */
    primaryButtonProps: IPrimaryButtonProps | INestedPrimaryButtonProps;
    /** Must be a single BdsRollupButton, or a fragment containing multiple BdsRollupButtons. These present as menuitems within the "More options" menu on the right side of the PrimaryButton split button.  */
    rolledPrimaryButtons?: ReactNode;
    /** Rendered directly below the title.  Commonly used for selectors such as "Purchase Orders" / "Bills" toggle on /app/PurchaseOrders. */
    bottom?: ReactNode;
    /** The number of filters applied to the list. */
    filterCount?: number;
    breadcrumbs?: BdsContentHeaderProps["breadcrumbs"];
    breadcrumbTestid?: BdsContentHeaderProps["breadcrumbTestid"];
} & (IWithStatusProps | IWithoutStatusProps); // A BdsRollupButton must have either all status props or no status props.

/**
 * Props for the primary action button when it is a single button (not nested). This would be the + Entity button that is by itself or part of a split button.
 */
type IPrimaryButtonProps = {
    id: string;
    text: string;
    /** By default, ariaLabel is derived from the textValue.  Only set this value if your primary button is not creating a new item. For example, on 'Time Clock' we will explicitly
     * set this value to 'Clock in' to avoid the default value of 'Create new Time Clock'.  */
    ariaLabel?: string;
    loading?: boolean;
    isHidden?: boolean;
    disabled?: boolean;
    target?: HTMLAttributeAnchorTarget;
    tooltip?: string;
    icon?: ReactNode;
} & IClickable;

/** Props for the primary action button when it is a nested button (has children). This would be the + Add button that has a dropdown */
interface INestedPrimaryButtonProps {
    id: string;
    loading?: boolean;
    isHidden?: boolean;
    disabled?: boolean;
    ariaLabel?: string;
    children?: ReactNode;
}

/** Type guard to check if props are INestedPrimaryButtonProps */
const isNestedPrimaryButtonProps = (
    props: IPrimaryButtonProps | INestedPrimaryButtonProps,
): props is INestedPrimaryButtonProps => {
    return "children" in props;
};

/** Either to or onClick must be defined for the Primary action button. */
type IClickable =
    | { to?: never; onClick: () => Promise<void> | void }
    | { to: BdsLocationDescriptor | string; onClick?: never };

interface IWithStatusProps {
    statusMessage: string;
    statusTestId: string;
    statusType: TBTStatusBadgeDisplayTypes;
}

interface IWithoutStatusProps {
    statusMessage?: never;
    statusTestId?: never;
    statusType?: never;
}

/** Default values for each actionIcon. */
const mappedActionIconDefaults: Record<
    PredefinedIconVariant,
    Omit<IFullIconActionProps, "onClick" | "to">
> = {
    feedback: {
        icon: <BdsIconGiveFeedback />,
        testid: "feedback",
        id: "feedback",
        displayType: "tertiary",
        value: "Feedback",
        text: "Feedback",
        ariaLabel: "Feedback",
        tooltip: "Give Feedback",
    },
    help: {
        icon: <BdsIconHelp />,
        testid: "help",
        id: "help",
        displayType: "tertiary",
        value: "Help",
        text: "Help",
        ariaLabel: "Help",
        tooltip: "Go to Help Center",
    },
    settings: {
        icon: <BdsIconSettings />,
        testid: "settings",
        id: "settings",
        displayType: "tertiary",
        value: "Settings",
        text: "Settings",
        ariaLabel: "Settings",
        tooltip: "Settings",
    },
    activity: {
        icon: <BdsIconClockCounterClockwise />,
        testid: "activity",
        id: "activity",
        displayType: "tertiary",
        value: "Activity",
        text: "Activity",
        ariaLabel: "Activity",
        tooltip: "Activity",
    },
    history: {
        icon: <BdsIconClockCounterClockwise />,
        testid: "history",
        id: "history",
        displayType: "tertiary",
        value: "History",
        text: "History",
        ariaLabel: "History",
        tooltip: "History",
    },
    print: {
        icon: <BdsIconPrinter />,
        testid: "print",
        id: "print",
        displayType: "tertiary",
        value: "Print",
        text: "Print",
        ariaLabel: "Print all",
        tooltip: "Print all",
    },
    export: {
        icon: <BdsIconExport />,
        testid: "export",
        id: "export",
        displayType: "tertiary",
        value: "Export",
        text: "Export",
        ariaLabel: "Export",
        tooltip: "Export all",
    },
    filter: {
        icon: <BdsIconFunnel />,
        testid: "filter",
        id: "filter",
        displayType: "tertiary",
        value: "Filter",
        text: "Filter",
        ariaLabel: "Filter",
        tooltip: "Filter",
    },
};

/** mappedActionIconDefaults defines the order of the presentation. Icons present in mappedActionIconDefaults are positioned to the right of icons that are not present. */
const sortActions = (a: IIconAction, b: IIconAction) => {
    const aIndex = iconVariantOrder.indexOf(a.icon);
    const bIndex = iconVariantOrder.indexOf(b.icon);

    // If both icons are in the order list, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
    }

    // If only one is in the order list, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither is in the order list, maintain original order
    return 0;
};

const renderBdsRollupButton = (props: IPrimaryButtonProps | INestedPrimaryButtonProps) => {
    if (props.isHidden) return;

    if (isNestedPrimaryButtonProps(props)) {
        return (
            <BdsRollupButton
                id={props.id}
                testid={props.id}
                displayType="primary"
                hotkey="ctrl+i"
                text="Add"
                ariaLabel={props.ariaLabel ?? "Add new item"}
                loading={props.loading}
                disabled={props.disabled}
                value={props.id}
                icon={<BdsIconPlus />}
            >
                {props.children}
            </BdsRollupButton>
        );
    } else {
        return (
            <BdsRollupButton
                displayType="primary"
                hotkey={props.disabled ? undefined : HotkeyCommands.new.command}
                tooltip={props.tooltip}
                id={props.id}
                testid={props.id}
                minStage={2}
                text={props.text}
                ariaLabel={props.ariaLabel ?? `Create new ${props.text}`}
                value={props.id}
                icon={props.icon ?? <BdsIconPlus />}
                onClick={props.onClick}
                to={props.to}
                loading={props.loading}
                disabled={props.disabled}
            />
        );
    }
};

/**
 * Recursively removes React Fragments from the children prop. This is needed because BdsRollupButton does not like receiving Fragments as children.
 * @param children The children prop to process.
 * @returns An array of React nodes without Fragments.
 */
function removeFragmentsFromChildren(children: React.ReactNode): ReactNode[] {
    const childrenArr = Children.toArray(children);
    const updatedChildrenArr = [];
    for (const child of childrenArr) {
        if (isValidElement(child) && child.type === Fragment) {
            updatedChildrenArr.push(...removeFragmentsFromChildren(child.props.children));
        } else {
            updatedChildrenArr.push(child);
        }
    }

    return updatedChildrenArr;
}

const DEFAULT_ICON_ACTIONS: IIconAction[] = [];

/**
 * A wrapper component for BdsContentHeader that standardizes the order of icon actions
 * for List pages. This ensures consistent user experience across all List pages by
 * presenting icons in the same order.
 */
export const BdsListPageHeader = ({
    title,
    jobName,
    testid,
    id = testid,
    className,
    entityType,
    statusMessage,
    statusTestId,
    statusType,
    iconActions = DEFAULT_ICON_ACTIONS,
    primaryButtonProps,
    bottom,
    filterCount,
    breadcrumbTestid,
    breadcrumbs,
    ...props
}: IBdsListPageHeader) => {
    const rolledPrimaryButtons = removeFragmentsFromChildren(props.rolledPrimaryButtons);
    const secondaryButtons = useMemo(
        () => removeFragmentsFromChildren(props.secondaryButtons),
        [props.secondaryButtons],
    );
    const sortedIconActions = useMemo(() => [...iconActions].sort(sortActions), [iconActions]);
    const primaryButton = useMemo(
        () => renderBdsRollupButton(primaryButtonProps),
        [primaryButtonProps],
    );
    const icons = useMemo(
        () =>
            sortedIconActions
                .filter((action) => !action.isHidden)
                .map((action, index) => {
                    const buttonProps: IFullIconActionProps = {
                        ...mappedActionIconDefaults[action.icon],
                        ...action,
                    };

                    if (action.icon === "filter") {
                        return (
                            <BdsListPageHeaderFilterButton
                                maxStage={1}
                                key={`${action.icon}-${index}`}
                                {...buttonProps}
                                minStage={1}
                                id={`${id}-${buttonProps.id}-content-header-button`}
                                testid={`${id}-${buttonProps.id}-content-header-button`}
                                filterCount={filterCount}
                                className="listpageheader-icon-hide"
                                icon={mappedActionIconDefaults[action.icon].icon}
                            />
                        );
                    }

                    return (
                        <BdsRollupButton
                            maxStage={1}
                            key={`${action.icon}-${index}`}
                            {...buttonProps}
                            id={`${id}-${buttonProps.id}-content-header-button`}
                            testid={`${id}-${buttonProps.id}-content-header-button`}
                            className="listpageheader-icon-hide"
                            icon={mappedActionIconDefaults[action.icon].icon}
                        />
                    );
                }),
        [sortedIconActions, id, filterCount],
    );
    // primaryButtons must be BdsRollupButton[]
    // primaryButtons are presented differently based on how many there are:
    // 1 - included in previous button group
    // 2+ - split button
    const leftArr = [...icons, secondaryButtons];
    if (rolledPrimaryButtons.length === 0 && primaryButton) {
        leftArr.push(primaryButton);
    }
    const slot = (
        <>
            {leftArr.length > 0 && (
                <BdsActionBar testid={`${id}-actionbar`}>
                    <BdsButtonGroup
                        id={`${id}-buttongroup`}
                        testid={`${id}-buttongroup`}
                        menuTestid={`${id}-buttongroup-menu`}
                        rollupTestid={`${id}-buttongroup-rollup`}
                        displayType="tertiary"
                        grow
                        flexAlign="end"
                        root="body"
                    >
                        {leftArr.flat()}
                    </BdsButtonGroup>
                    {/* dont render rolled buttons if there is no primaryButton. e.g. the user doesnt have permissions to see the primary button */}
                    {rolledPrimaryButtons.length > 0 && primaryButton && (
                        <BdsButtonGroup
                            flexAlign="end"
                            id={`${id}-split-buttongroup`}
                            testid={`${id}-split-buttongroup`}
                            menuTestid={`${id}-split-buttongroup-menu`}
                            rollupTestid={`${id}-split-buttongroup-rollup`}
                            splitButton
                        >
                            {primaryButton}
                            <BdsRollupButton
                                // todo: reconcine id + testid
                                id={`${id}-rolled-primary-buttons`}
                                testid={`${id}-rolled-primary-buttons`}
                                displayType="primary"
                                value="More create options"
                                ariaLabel="More create options"
                            >
                                {rolledPrimaryButtons}
                            </BdsRollupButton>
                        </BdsButtonGroup>
                    )}
                </BdsActionBar>
            )}
        </>
    );

    const bdsContentHeaderProps: BdsContentHeaderProps = {
        id,
        testid,
        className: cn("bds-listpage-header", className),
        title,
        slotTop: slot,
        actionBar: bottom,
        jobName: jobName || <div />,
        entityType,
    };

    if (breadcrumbs && breadcrumbTestid) {
        Object.assign(bdsContentHeaderProps, {
            breadcrumbs,
            breadcrumbTestid,
        });
    }

    // status badges require all 3 props
    if (statusMessage && statusTestId && statusType) {
        Object.assign(bdsContentHeaderProps, {
            statusMessage,
            statusTestId,
            statusType,
        });
    }
    return <BdsContentHeader {...bdsContentHeaderProps} />;
};

export default BdsListPageHeader;
